import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /**
   * Animation frames to wait for `.cart-drawer__summary` to appear before giving up.
   * The summary arrives with the Section Rendering response, which can land after
   * `theme-drawer:open` fires. ~12 frames is roughly 200ms at 60fps.
   * @type {number}
   */
  #summarySettleFrames = 12;

  /** @type {number | null} */
  #stickyRafId = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The sticky ratio depends on the drawer's own height, so it has to be
    // recomputed whenever the viewport changes. visualViewport covers the
    // mobile URL-bar collapse, which does not fire a window resize.
    window.addEventListener('resize', this.#handleViewportChange);
    window.addEventListener('orientationchange', this.#handleViewportChange);
    window.visualViewport?.addEventListener('resize', this.#handleViewportChange);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    window.removeEventListener('resize', this.#handleViewportChange);
    window.removeEventListener('orientationchange', this.#handleViewportChange);
    window.visualViewport?.removeEventListener('resize', this.#handleViewportChange);

    this.#cancelPendingStickyUpdate();
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * Recomputes the sticky state after a viewport change, coalesced to one
   * measurement per frame. Only meaningful while the drawer is open.
   */
  #handleViewportChange = () => {
    if (!this.#themeDrawer?.isOpen) return;

    this.#cancelPendingStickyUpdate();
    this.#stickyRafId = requestAnimationFrame(() => {
      this.#stickyRafId = null;
      this.#updateStickyState();
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // The empty-cart guard is gone: the drawer used to refuse to pre-open on an
    // empty cart because it would have flashed "Your cart is empty" over an item
    // the customer had just added. product-form.js now puts the drawer into its
    // busy state for the length of the add, which replaces that copy with a
    // placeholder row — so the empty cart gets the same instant open as any
    // other, instead of sitting still until the response lands.
    if (shouldAutoOpen && !sourceModal) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          // Add happened while the drawer was already open (e.g. cart upsells) —
          // scroll to the top so the new line item's entry animation is visible.
          if (event.action === 'add' && !detail?.didError && this.#themeDrawer?.isOpen) {
            requestAnimationFrame(() => this.#scrollItemsToTop());
          }
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /**
   * Scrolls the drawer's item list back to the top, where newly added items appear.
   */
  #scrollItemsToTop() {
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';

    for (const selector of ['.cart-drawer__content', '.cart-drawer__items']) {
      this.#dialog?.querySelector(selector)?.scrollTo({ top: 0, behavior });
    }
  }

  #cancelPendingStickyUpdate() {
    if (this.#stickyRafId !== null) {
      cancelAnimationFrame(this.#stickyRafId);
      this.#stickyRafId = null;
    }
  }

  /**
   * Sets `cart-summary-sticky` on the dialog, which decides whether the summary
   * (and the checkout button inside it) stays pinned to the bottom of the drawer.
   *
   * `false` unpins the summary and disables the item list's own scroller, so a
   * wrong value pushes the checkout button down the content flow — below the fold
   * on shorter viewports, with no way to scroll to it. Nothing else in the theme
   * recomputes this attribute, so a wrong value persists for the whole session.
   *
   * @param {number} [attemptsLeft] - Remaining retry frames while the summary is absent.
   */
  #updateStickyState(attemptsLeft = this.#summarySettleFrames) {
    this.#cancelPendingStickyUpdate();

    const dialog = this.#dialog;
    if (!dialog) return;

    // An empty cart genuinely has no summary. That is the documented case for
    // unsticky mode and there is no checkout button to strand, so settle now.
    if (this.#isCartEmpty()) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');
    const drawerHeight = dialog.getBoundingClientRect().height;

    // A non-empty cart with no summary, or a dialog with no height, means we are
    // measuring mid Section-Rendering swap or before the dialog has laid out.
    // Retry on the next frame rather than latching "false" — see above.
    if (!content || !summary || drawerHeight === 0) {
      if (attemptsLeft > 0) {
        this.#stickyRafId = requestAnimationFrame(() => {
          this.#stickyRafId = null;
          this.#updateStickyState(attemptsLeft - 1);
        });
      }

      // Out of retries: leave the attribute untouched. cart-drawer.liquid ships
      // `cart-summary-sticky="true"`, which keeps the checkout button pinned.
      return;
    }

    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
